<template>
    <div id="kirmizi-santral-cdr">
        <div class="page-header">
            <div class="left">
                <h3>Arama Kayıtları (CDR)</h3>
                <p>Kırmızı Santral arama detay kayıtlarını görüntüleyin</p>
            </div>
            <div class="right">
                <a-button @click="refreshData" :loading="loading" :icon="'reload'"> Yenile </a-button>
                <a-button type="primary" @click="exportCDR" :loading="loading" :icon="'download'">
                    Dışa Aktar
                </a-button>
            </div>
        </div>

        <!-- Advanced Filters -->
        <div class="toolbar">
            <div class="toolbar-filter">
                <a-input-search
                    style="width: 300px"
                    v-model="searchText"
                    placeholder="CDR ID, Numara, Kaynak Adı veya Unique ID ile ara..."
                    class="search-input"
                    :loading="loading"
                    @search="onSearch"
                />
                <!-- Date Range Filter (Always visible and required) -->
                <div class="date-filter-inline">
                    <span class="date-label">Tarih Aralığı:</span>
                    <a-range-picker
                        v-model="dateRange"
                        format="DD.MM.YYYY"
                        style="width: 250px"
                        :placeholder="['Başlangıç', 'Bitiş']"
                        @change="onDateRangeChange"
                    />
                    <span class="date-required">*</span>
                </div>
                <CommonFilterTool
                    v-model="breakdownFilter"
                    :keep-state-on-query-params="false"
                    :filter-meta="filterMetas"
                    :label-formatter="filterLabelFormatter"
                    @addFilter="addFilter"
                    @removeFilter="removeFilter"
                    @clearAll="clearFilters"
                    :show-clear-all="breakdownFilter.length > 0"
                />
            </div>
            <div class="toolbar-actions"></div>
        </div>

        <!-- Statistics -->
        <div class="statistics">
            <div class="stats-grid">
                <div class="stat-card total-calls">
                    <div class="stat-icon">
                        <i class="material-icons">call</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.totalCalls }}</div>
                        <div class="stat-label">Toplam Arama</div>
                    </div>
                </div>

                <div class="stat-card answered-calls">
                    <div class="stat-icon">
                        <i class="material-icons">call_received</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.answeredCalls }}</div>
                        <div class="stat-label">Cevaplanmış</div>
                        <div class="stat-percentage">
                            {{
                                statistics.totalCalls > 0
                                    ? Math.round((statistics.answeredCalls / statistics.totalCalls) * 100)
                                    : 0
                            }}%
                        </div>
                    </div>
                </div>

                <div class="stat-card total-duration">
                    <div class="stat-icon">
                        <i class="material-icons">schedule</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.totalDuration }}</div>
                        <div class="stat-label">Toplam Süre</div>
                        <div class="stat-unit">dakika</div>
                    </div>
                </div>

                <div class="stat-card average-duration">
                    <div class="stat-icon">
                        <i class="material-icons">timer</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.averageDuration }}</div>
                        <div class="stat-label">Ortalama Süre</div>
                        <div class="stat-unit">dakika</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- CDR Table -->
        <div class="content">
            <SupsisTable
                :columns="getCols()"
                :rows="cdrRecords"
                :loading="loading"
                top-pagination
                bottom-pagination
                :pagination-cursor="pagination"
                :options="{
                    fixedHeader: true,
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="onPaginationChange"
                :pagination-options="[10, 20, 30, 40, 50, 100, 200, 300, 400, 500, 1000]"
            />
        </div>

        <!-- Call Detail Modal -->
        <a-modal v-model="showDetailModal" title="Arama Detayları" :footer="null" width="700px">
            <div v-if="selectedCall">
                <a-descriptions :column="2" bordered size="small">
                    <a-descriptions-item label="CDR ID">
                        {{ selectedCall.cdrid }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Tarih">
                        {{ $moment(selectedCall.calldate).format("DD.MM.YYYY HH:mm:ss") }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Arayan (CLI)">
                        {{ selectedCall.cli }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Aranan (CLD)">
                        {{ selectedCall.cld }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Yön">
                        <a-tag :color="getDirectionColor(selectedCall.direction)">
                            {{ getDirectionText(selectedCall.direction) }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="Durum">
                        <a-tag :color="getStatusColor(selectedCall.status)">
                            {{ selectedCall.status }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="Toplam Süre">
                        {{ formatDuration(selectedCall.duration) }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Konuşma Süresi">
                        {{ formatDuration(selectedCall.billsec) }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Çalma Süresi">
                        {{ formatDuration(selectedCall.ringtime) }}
                    </a-descriptions-item>
                    <a-descriptions-item label="SIP Kodu">
                        {{ selectedCall.sipcode }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Uygulama">
                        {{ selectedCall.application || "-" }}
                    </a-descriptions-item>
                    <a-descriptions-item label="DID">
                        {{ selectedCall.did || "-" }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Unique ID" span="2">
                        {{ selectedCall.uniqueid }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Kayıt Dosyası" span="2" v-if="selectedCall.recordingfile">
                        <a-button size="small" @click="playRecording(selectedCall)" :icon="'play-circle'">
                            Kayıt Dinle
                        </a-button>
                    </a-descriptions-item>
                </a-descriptions>
            </div>
        </a-modal>

        <!-- Audio Player Bottom Dock -->
        <div class="audio-player-dock" v-if="showAudioPlayer">
            <div class="audio-player-container">
                <div class="audio-player-info">
                    <i class="material-icons audio-icon pulse">music_note</i>
                    <div class="audio-title-container">
                        <div class="now-playing">Şimdi Çalıyor</div>
                        <div class="audio-title">{{ currentPlayingRecordName }}</div>
                    </div>
                </div>
                <div class="audio-player-wrapper">
                    <audio controls :src="currentPlayingRecordURL" autoplay ref="audioPlayer"></audio>
                </div>
                <div class="audio-player-controls">
                    <a-button type="danger" shape="circle" icon="close" size="large" @click="closeAudioPlayer" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import CommonFilterTool from "../../../Chatbot/pages/archive/CommonFilterTool.vue";
import { WEBPHONE_RS_API } from "../../../../../api/webphone.redsantral";
import log2 from "../../../../../utils/log2";

const log = log2("KirmiziSantralCDR");

export default Vue.extend({
    name: "KirmiziSantralCDR",
    components: {
        SupsisTable,
        SupsisButton,
        CommonFilterTool,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            showDetailModal: false,
            selectedCall: null,
            cdrRecords: [],
            searchText: "",
            dateRange: [moment(), moment()], // Default to today
            pagination: {
                page: 1,
                perPage: 1000,
                total: 0,
            },
            statistics: {
                totalCalls: 0,
                answeredCalls: 0,
                totalDuration: 0,
                averageDuration: 0,
            },
            // Audio player dock
            showAudioPlayer: false,
            currentPlayingRecordURL: "",
            currentPlayingRecordName: "",
            // Filter system using CommonFilterTool
            filterMetas: [
                {
                    name: "direction",
                    label: "Arama Yönü",
                    materialIcon: "call_split",
                    controlType: "select",
                    defaultValue: null,
                    options: () => [
                        { id: "INBOUND", label: "Gelen" },
                        { id: "OUTBOUND", label: "Giden" },
                    ],
                },
                {
                    name: "status",
                    label: "Arama Durumu",
                    materialIcon: "info",
                    controlType: "select",
                    defaultValue: null,
                    options: () => [
                        { id: "ANSWERED", label: "Cevaplanmış" },
                        { id: "NO ANSWER", label: "Cevap Yok" },
                        { id: "BUSY", label: "Meşgul" },
                        { id: "FAILED", label: "Başarısız" },
                    ],
                },
                {
                    name: "cli",
                    label: "Arayan Numara",
                    materialIcon: "phone",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "cld",
                    label: "Aranan Numara",
                    materialIcon: "phone_forwarded",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "application",
                    label: "Uygulama",
                    materialIcon: "apps",
                    controlType: "select",
                    defaultValue: null,
                    options: () => [
                        { id: "TRUNK", label: "TRUNK" },
                        { id: "QUEUE", label: "QUEUE" },
                        { id: "EXTENSION", label: "EXTENSION" },
                    ],
                },
                {
                    name: "sipcode",
                    label: "SIP Kodu",
                    materialIcon: "code",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "duration_min",
                    label: "Min. Süre (saniye)",
                    materialIcon: "timer",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "duration_max",
                    label: "Max. Süre (saniye)",
                    materialIcon: "timer",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "billsec_min",
                    label: "Min. Konuşma (saniye)",
                    materialIcon: "schedule",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "billsec_max",
                    label: "Max. Konuşma (saniye)",
                    materialIcon: "schedule",
                    controlType: "input",
                    defaultValue: null,
                },
            ],
            breakdownFilter: [],
        };
    },
    methods: {
        getCols() {
            return [
                {
                    key: "calldate",
                    field: "calldate",
                    title: "Tarih/Saat",
                    width: 140,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.$moment(row.calldate).format("DD.MM.YYYY HH:mm")}</span>;
                    },
                },
                {
                    key: "direction",
                    field: "direction",
                    title: "Yön",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={this.getDirectionColor(row.direction)}>
                                {this.getDirectionText(row.direction)}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "cli",
                    field: "cli",
                    title: "Arayan",
                    width: 120,
                    align: "left",
                },
                {
                    key: "cld",
                    field: "cld",
                    title: "Aranan",
                    width: 120,
                    align: "left",
                },
                {
                    key: "status",
                    field: "status",
                    title: "Durum",
                    width: 100,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <a-tag color={this.getStatusColor(row.status)}>{row.status}</a-tag>;
                    },
                },
                {
                    key: "duration",
                    field: "duration",
                    title: "Toplam Süre",
                    width: 100,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.formatDuration(row.duration)}</span>;
                    },
                },
                {
                    key: "billsec",
                    field: "billsec",
                    title: "Konuşma",
                    width: 90,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.formatDuration(row.billsec)}</span>;
                    },
                },
                {
                    key: "ringtime",
                    field: "ringtime",
                    title: "Çalma",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.formatDuration(row.ringtime)}</span>;
                    },
                },
                {
                    key: "application",
                    field: "application",
                    title: "Uygulama",
                    width: 100,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span>{row.application || "-"}</span>;
                    },
                },
                {
                    key: "sipcode",
                    field: "sipcode",
                    title: "SIP Kodu",
                    width: 100,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span>{row.sipcode || "-"}</span>;
                    },
                },
                {
                    key: "recordingfile",
                    field: "recordingfile",
                    title: "Kayıt",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        if (!row.recordingfile) return <span>-</span>;

                        return (
                            <SupsisButton
                                onClick={() => this.playRecording(row)}
                                small
                                content="Kayıt Dinle"
                                v-tippy={{ followCursor: true, placement: "bottom-end" }}
                            >
                                <i class="material-icons text-primary">play_circle</i>
                            </SupsisButton>
                        );
                    },
                },
                {
                    title: "İşlemler",
                    key: "actions",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <SupsisButton
                                onClick={() => this.viewDetails(row)}
                                small
                                content="Detay Görüntüle"
                                v-tippy={{ followCursor: true, placement: "bottom-end" }}
                            >
                                <i class="material-icons text-primary">visibility</i>
                            </SupsisButton>
                        );
                    },
                },
            ];
        },
        async loadCDRData() {
            if (!this.dateRange || this.dateRange.length !== 2) {
                this.$message.error("Lütfen tarih aralığı seçin");
                return;
            }

            try {
                this.loading = true;

                const [startDate, endDate] = this.dateRange;
                const filters = {
                    startdate: startDate.format("YYYY-MM-DD"),
                    enddate: endDate.format("YYYY-MM-DD"),
                    limit: this.pagination.perPage.toString(),
                    offset: ((this.pagination.page - 1) * this.pagination.perPage).toString(),
                    ...this.generateAPIFilters(),
                };

                // Add search term if provided
                if (this.searchText && this.searchText.trim()) {
                    filters.searchText = this.searchText.trim();
                }

                log("Loading CDR with filters:", filters);

                const response = await WEBPHONE_RS_API.reports.getCDRList(filters);

                if (response && response.cdrs) {
                    this.cdrRecords = response.cdrs.map((cdr, index) => ({
                        ...cdr,
                        _id: cdr.uniqueid || index.toString(),
                        // Convert duration strings to numbers
                        duration: parseInt(cdr.duration) || 0,
                        billsec: parseInt(cdr.billsec) || 0,
                        ringtime: parseInt(cdr.ringtime) || 0,
                    }));

                    // Update pagination with new total (reactive update)
                    this.pagination = {
                        ...this.pagination,
                        total: response.total || this.cdrRecords.length,
                    };
                } else {
                    this.cdrRecords = [];
                    this.pagination = {
                        ...this.pagination,
                        total: 0,
                    };
                }

                this.calculateStatistics(this.cdrRecords);
            } catch (error) {
                console.error("CDR verileri yüklenirken hata:", error);
                this.$message.error("CDR verileri yüklenirken hata oluştu");
                this.cdrRecords = [];
                this.pagination.total = 0;
            } finally {
                this.loading = false;
            }
        },
        generateAPIFilters() {
            const apiFilters = {};

            this.breakdownFilter.forEach((filter) => {
                if (filter.filterValue !== null && filter.filterValue !== undefined && filter.filterValue !== "") {
                    apiFilters[filter.name] = filter.filterValue;
                }
            });

            return apiFilters;
        },
        calculateStatistics(records) {
            this.statistics.totalCalls = records.length;
            this.statistics.answeredCalls = records.filter((r) => r.status === "ANSWERED").length;

            const totalDurationSeconds = records.reduce((sum, call) => sum + (call.billsec || 0), 0);
            this.statistics.totalDuration = Math.round((totalDurationSeconds / 60) * 10) / 10;

            const answeredCalls = records.filter((r) => r.status === "ANSWERED");
            if (answeredCalls.length > 0) {
                const avgDurationSeconds =
                    answeredCalls.reduce((sum, call) => sum + (call.billsec || 0), 0) / answeredCalls.length;
                this.statistics.averageDuration = Math.round((avgDurationSeconds / 60) * 10) / 10;
            } else {
                this.statistics.averageDuration = 0;
            }
        },
        onDateRangeChange() {
            this.pagination.page = 1;
            this.loadCDRData();
        },
        onSearch() {
            this.pagination.page = 1;
            this.loadCDRData();
        },
        onPaginationChange(newPagination) {
            log.info("onPaginationChange", newPagination);

            this.pagination = {
                ...newPagination,
                total: this.pagination.total, // Mevcut total'ı koru
            };
            this.loadCDRData();
        },
        addFilter(name, value) {
            log.info("addFilter", name, value);
            const meta = this.filterMetas.find((x) => x.name === name);
            const filter = this.breakdownFilter.find((x) => x.name === name);

            if (filter) {
                this.breakdownFilter = this.breakdownFilter.map((x) => {
                    if (x.name === name) {
                        x.filterValue = value || meta.defaultValue;
                    }
                    return x;
                });
            } else {
                this.breakdownFilter.push({
                    name: meta.name,
                    label: meta.label,
                    filterValue: value || meta.defaultValue,
                    materialIcon: meta.materialIcon,
                    controlType: meta.controlType,
                    options: meta.options,
                });
            }

            // Force reload when filter is added
            //this.pagination.page = 1;
            //this.loadCDRData();
        },
        removeFilter(name) {
            log.info("removeFilter", name);
            this.breakdownFilter = this.breakdownFilter.filter((x) => x.name !== name);

            // Force reload when filter is removed
            //this.pagination.page = 1;
            //this.loadCDRData();
        },
        filterLabelFormatter(name, value) {
            const filter = this.filterMetas.find((x) => x.name === name);
            log.info("filterLabelFormatter", { name, value, filter });

            const isSelect = filter.controlType === "select";
            if (isSelect) {
                const option = filter.options().find((x) => x.id === value);
                if (option) {
                    return `${filter.label}: ${option.label}`;
                }
            }

            if (value) {
                return `${filter.label}: ${value}`;
            }
            return filter.label;
        },
        clearFilters() {
            log.info("clearFilters");
            this.breakdownFilter = [];
            this.searchText = "";
            this.dateRange = [moment(), moment()]; // Reset to today
            //this.pagination.page = 1;
            //this.loadCDRData();
        },
        refreshData() {
            this.loadCDRData();
        },
        exportCDR() {
            if (this.cdrRecords.length === 0) {
                this.$message.warning("Dışa aktarılacak veri bulunamadı");
                return;
            }

            const csvContent = this.generateCSV(this.cdrRecords);
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `cdr_export_${moment().format("YYYY-MM-DD")}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.$message.success(`${this.cdrRecords.length} kayıt dışa aktarıldı`);
        },
        generateCSV(data) {
            const headers = [
                "CDR ID",
                "Tarih",
                "Yön",
                "Arayan",
                "Aranan",
                "Durum",
                "Toplam Süre",
                "Konuşma Süresi",
                "SIP Kodu",
            ];
            const rows = data.map((record) => [
                record.cdrid,
                moment(record.calldate).format("DD.MM.YYYY HH:mm:ss"),
                this.getDirectionText(record.direction),
                record.cli,
                record.cld,
                record.status,
                this.formatDuration(record.duration),
                this.formatDuration(record.billsec),
                record.sipcode || "",
            ]);

            return [headers, ...rows].map((row) => row.join(",")).join("\n");
        },
        viewDetails(call) {
            this.selectedCall = call;
            this.showDetailModal = true;
        },
        async playRecording(call) {
            if (!call.recordingfile) {
                this.$message.error("Bu aramaya ait kayıt dosyası bulunamadı");
                return;
            }

            // Eğer recordurl varsa direkt onu kullan
            if (call.recordurl) {
                this.currentPlayingRecordURL = call.recordurl;
                this.currentPlayingRecordName =
                    this.$moment(call.calldate).format("DD.MM.YYYY HH:mm") + " - " + call.cli + " → " + call.cld;
                this.showAudioPlayer = true;

                // Focus on audio player
                this.$nextTick(() => {
                    if (this.$refs.audioPlayer) {
                        this.$refs.audioPlayer.focus();
                    }
                });
                return;
            }

            // recordurl yoksa API'den al (fallback)
            try {
                const params = {
                    startdate: moment(call.calldate).format("YYYY-MM-DD"),
                    enddate: moment(call.calldate).format("YYYY-MM-DD"),
                    uniqueid: call.uniqueid,
                    recordingfile: call.recordingfile,
                };

                const response = await WEBPHONE_RS_API.reports.getRecordingFile(params);

                if (response && response.recording && response.recording.recordurl) {
                    // Cache the URL for future use
                    call.recordurl = response.recording.recordurl;

                    this.currentPlayingRecordURL = response.recording.recordurl;
                    this.currentPlayingRecordName =
                        this.$moment(call.calldate).format("DD.MM.YYYY HH:mm") + " - " + call.cli + " → " + call.cld;
                    this.showAudioPlayer = true;

                    // Focus on audio player
                    this.$nextTick(() => {
                        if (this.$refs.audioPlayer) {
                            this.$refs.audioPlayer.focus();
                        }
                    });
                } else {
                    this.$message.error("Kayıt dosyası URL'i alınamadı");
                }
            } catch (error) {
                console.error("Kayıt dosyası yüklenirken hata:", error);
                this.$message.error("Kayıt dosyası yüklenirken hata oluştu");
            }
        },
        closeAudioPlayer() {
            // Pause audio and reset
            if (this.$refs.audioPlayer) {
                this.$refs.audioPlayer.pause();
            }
            this.showAudioPlayer = false;
            this.currentPlayingRecordURL = "";
            this.currentPlayingRecordName = "";
        },
        getDirectionText(direction) {
            const directions = {
                INBOUND: "Gelen",
                OUTBOUND: "Giden",
            };
            return directions[direction] || direction;
        },
        getDirectionColor(direction) {
            const colors = {
                INBOUND: "green",
                OUTBOUND: "blue",
            };
            return colors[direction] || "default";
        },
        getStatusColor(status) {
            const colors = {
                ANSWERED: "green",
                "NO ANSWER": "orange",
                BUSY: "red",
                FAILED: "red",
            };
            return colors[status] || "default";
        },
        formatDuration(seconds) {
            const duration = parseInt(seconds) || 0;
            if (duration === 0) return "0s";
            const minutes = Math.floor(duration / 60);
            const secs = duration % 60;
            return minutes > 0 ? `${minutes}dk ${secs}s` : `${secs}s`;
        },
    },
    mounted() {
        this.loadCDRData();
    },
    beforeDestroy() {
        // Sayfa kapanırken ses oynatıcıyı temizle
        this.closeAudioPlayer();
    },
    watch: {
        // Modal açıldığında/kapatıldığında ses oynatıcılarını kontrol et
        showDetailModal(newVal) {
            if (!newVal) {
                // Modal kapandığında dock audio player'ı çalışır durumda bırak
            } else {
                // Modal açıldığında dock audio player'ı durdur
                if (this.showAudioPlayer && this.$refs.audioPlayer) {
                    this.$refs.audioPlayer.pause();
                }
            }
        },
        breakdownFilter: {
            handler() {
                this.pagination.page = 1;
                this.loadCDRData();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
#kirmizi-santral-cdr {
    padding: 20px;
    background: var(--color-bg-default);
    height: 100%;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            h3 {
                margin: 0 0 5px 0;
                color: var(--color-text-primary);
                font-size: 20px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: var(--color-text-secondary);
                font-size: 14px;
            }
        }

        .right {
            display: flex;
            gap: 10px;
        }
    }

    .toolbar {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 0;
        gap: 5px;
        margin-bottom: 20px;

        .toolbar-filter {
            display: flex;
            align-items: center;
            gap: 15px;
            flex: 1;
            flex-wrap: wrap;

            .search-input {
                margin: 0 !important;
            }

            .date-filter-inline {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                background: #f5f5f5;
                border-radius: 6px;
                border: 1px solid #d9d9d9;

                .date-label {
                    font-weight: 500;
                    color: #333;
                    white-space: nowrap;
                }

                .date-required {
                    color: #ff4d4f;
                    font-weight: bold;
                }
            }
        }

        .toolbar-actions {
            display: flex;
            gap: 5px;
        }

        .common-filter-tool {
            border-bottom: none !important;
            padding: unset !important;
        }
    }

    .statistics {
        margin-bottom: 20px;

        .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;

            .stat-card {
                background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
                border-radius: 16px;
                padding: 24px;
                display: flex;
                align-items: center;
                gap: 20px;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                border: 1px solid #e2e8f0;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                overflow: hidden;

                &::before {
                    content: "";
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    transition: height 0.3s ease;
                }

                &:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);

                    &::before {
                        height: 6px;
                    }

                    .stat-icon {
                        transform: scale(1.1);
                    }
                }

                &.total-calls {
                    &::before {
                        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                        color: white;
                    }

                    .stat-value {
                        color: #1d4ed8;
                    }
                }

                &.answered-calls {
                    &::before {
                        background: linear-gradient(135deg, #10b981, #059669);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #10b981, #059669);
                        color: white;
                    }

                    .stat-value {
                        color: #059669;
                    }

                    .stat-percentage {
                        color: #10b981;
                        font-size: 12px;
                        font-weight: 600;
                        margin-top: 4px;
                    }
                }

                &.total-duration {
                    &::before {
                        background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                        color: white;
                    }

                    .stat-value {
                        color: #7c3aed;
                    }
                }

                &.average-duration {
                    &::before {
                        background: linear-gradient(135deg, #f59e0b, #d97706);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #f59e0b, #d97706);
                        color: white;
                    }

                    .stat-value {
                        color: #d97706;
                    }
                }

                .stat-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    transition: all 0.3s ease;

                    .material-icons {
                        font-size: 28px;
                    }
                }

                .stat-content {
                    flex: 1;
                    min-width: 0;

                    .stat-value {
                        font-size: 32px;
                        font-weight: 700;
                        line-height: 1.2;
                        margin-bottom: 4px;
                        font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
                    }

                    .stat-label {
                        font-size: 14px;
                        font-weight: 500;
                        color: #64748b;
                        margin-bottom: 2px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }

                    .stat-unit {
                        font-size: 12px;
                        color: #94a3b8;
                        font-weight: 500;
                    }
                }
            }
        }

        // Responsive design
        @media (max-width: 768px) {
            .stats-grid {
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 15px;

                .stat-card {
                    padding: 20px;
                    gap: 15px;

                    .stat-icon {
                        width: 48px;
                        height: 48px;
                        border-radius: 12px;

                        .material-icons {
                            font-size: 24px;
                        }
                    }

                    .stat-content .stat-value {
                        font-size: 28px;
                    }
                }
            }
        }

        @media (max-width: 480px) {
            .stats-grid {
                grid-template-columns: 1fr;
                gap: 12px;

                .stat-card {
                    padding: 16px;
                    gap: 12px;

                    .stat-icon {
                        width: 40px;
                        height: 40px;
                        border-radius: 10px;

                        .material-icons {
                            font-size: 20px;
                        }
                    }

                    .stat-content .stat-value {
                        font-size: 24px;
                    }
                }
            }
        }
    }

    .content {
        background: var(--color-bg-container);
        border-radius: 6px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;
    }

    .audio-player-dock {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 15px 0;
        z-index: 1000;

        .audio-player-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            max-width: 800px;
            margin: 0 auto;
            padding: 15px 25px;
            background: rgb(255 229 224);
            border-radius: 12px;

            .audio-player-info {
                display: flex;
                align-items: center;
                gap: 12px;
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                .audio-icon {
                    font-size: 28px;
                    color: #3a7bd5;
                    background: white;
                    border-radius: 50%;
                    padding: 8px;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
                    animation: pulse 2s infinite;
                }

                .audio-title-container {
                    .now-playing {
                        font-weight: 600;
                        color: #3a7bd5;
                        font-size: 12px;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        margin-bottom: 3px;
                    }

                    .audio-title {
                        font-weight: 500;
                        color: #333;
                        font-size: 14px;
                    }
                }
            }

            .audio-player-wrapper {
                flex: 2;
                max-width: 500px;
                padding: 0 20px;

                audio {
                    width: 100%;
                    height: 40px;
                    border-radius: 8px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
            }

            .audio-player-controls {
                margin-left: 15px;

                button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
                    transition: all 0.3s ease;

                    &:hover {
                        transform: scale(1.1);
                        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
                    }
                }
            }
        }
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        }
        50% {
            transform: scale(1.05);
            box-shadow: 0 6px 15px rgba(58, 123, 213, 0.4);
        }
        100% {
            transform: scale(1);
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        }
    }
}
</style>
